import React from 'react';
import useUpdateEffect from './useUpdateEffect';
import {Dates} from '../utils';

// LibreWXR serves the RainViewer v2-compatible weather-maps schema, so the
// shapes below fit both APIs. Tile URLs must always be composed from the
// response `host` + item `path` — path formats are backend-controlled and
// have changed before (RainViewer moved from timestamp to hash paths).
export type RainViewerItem = {
  time: number;
  path: string;
};

export type RainViewerApiResponse = {
  version: string;
  generated: number;
  host: string;
  radar: {
    past: RainViewerItem[];
    nowcast: RainViewerItem[];
  };
  satellite: {
    infrared: RainViewerItem[];
  };
};

const WEATHER_MAPS_URL = 'https://radar.smartmixin.io/public/weather-maps.json';

const findClosestIndex = (target: number, timestamps: number[]) => {
  if (timestamps.length > 0) {
    const closest = timestamps.reduce((prev, curr) => {
      return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
    });
    return timestamps.findIndex((item) => closest === item);
  }
  return 0;
};

const REFRESH_INTERVAL_MS = 6 * 60 * 1000; // 6 minutes

const useRainRadar = (
  isFocused: boolean,
  initialPlaying: boolean,
  syncWithNow = true,
  includeNowcast = true,
) => {
  const [timestamps, setTimestamps] = React.useState<number[]>([]);
  const [rainRadarItems, setRainRadarItems] =
    React.useState<Record<number, string>>();
  const [host, setHost] = React.useState<string>();
  const [index, setIndex] = React.useState<number>(
    syncWithNow ? findClosestIndex(Dates.now(), timestamps) : 0,
  );
  const [currentTimestamp, setCurrentTimestamp] = React.useState<number>();
  const [nextTimestamp, setNextTimestamp] = React.useState<number>();
  const isUpdatingRef = React.useRef(false);
  const includeNowcastRef = React.useRef(includeNowcast);
  includeNowcastRef.current = includeNowcast;

  const fetchRadarData = React.useCallback(async () => {
    // Prevent concurrent calls
    if (isUpdatingRef.current) {
      return;
    }

    isUpdatingRef.current = true;
    try {
      const response = await fetch(WEATHER_MAPS_URL);
      if (!response.ok) {
        return;
      }
      const data = (await response.json()) as RainViewerApiResponse;
      setHost(data.host);
      const past = data.radar.past;
      const nowcast = includeNowcastRef.current ? data.radar.nowcast : [];
      setTimestamps(
        past.map((item) => item.time).concat(nowcast.map((item) => item.time)),
      );
      const rainItems = past.concat(nowcast).reduce(
        (obj, item) => {
          obj[item.time] = item.path;
          return obj;
        },
        {} as Record<number, string>,
      );

      setRainRadarItems(rainItems);
      setIndex(syncWithNow ? past.length - 1 : 0);
    } catch (error) {
      // Silent error handling to match existing behavior
    } finally {
      isUpdatingRef.current = false;
      setLastDataFetch(Dates.now());
    }
  }, [syncWithNow]);

  const [playing, setPlaying] = React.useState(initialPlaying);

  const [lastDataFetch, setLastDataFetch] = React.useState(Dates.now());

  const setTimestamp = React.useCallback(
    (value: number) => {
      const newIndex = findClosestIndex(value, timestamps);
      setIndex(newIndex);
    },
    [timestamps],
  );

  React.useEffect(() => {
    if (isFocused && lastDataFetch) {
      const elapsedMs = (Dates.now() - lastDataFetch) * 1000;
      if (elapsedMs >= REFRESH_INTERVAL_MS) {
        fetchRadarData();
      } else {
        const remainingMs = REFRESH_INTERVAL_MS - elapsedMs;
        const timerId = setTimeout(() => {
          fetchRadarData();
        }, remainingMs);
        return () => clearTimeout(timerId);
      }
    }
  }, [fetchRadarData, isFocused, lastDataFetch]);

  React.useEffect(() => {
    fetchRadarData();
  }, [fetchRadarData]);

  React.useEffect(() => {
    if (isFocused && timestamps.length > 0 && playing) {
      const timerId = setInterval(() => {
        setIndex((i) => (i + 1) % timestamps.length);
      }, 750);

      return () => clearInterval(timerId);
    }
  }, [timestamps, isFocused, playing]);

  useUpdateEffect(() => {
    if (isFocused && timestamps.length > 0) {
      setCurrentTimestamp(timestamps[index]);
      setNextTimestamp(
        index < timestamps.length - 1 ? timestamps[index + 1] : timestamps[0],
      );
    }
  }, [isFocused, index, timestamps]);

  return React.useMemo(
    () => ({
      currentTimestamp,
      nextTimestamp,
      timestamps,
      setTimestamp,
      rainRadarItems,
      host,
      playing,
      setPlaying,
    }),
    [
      currentTimestamp,
      nextTimestamp,
      timestamps,
      setTimestamp,
      rainRadarItems,
      host,
      playing,
      setPlaying,
    ],
  );
};

export default useRainRadar;
